import { useEffect, useState } from 'react';
import type { FieldsProps } from './DestinationFields';
import { FieldMapEditor } from './FieldMapEditor';
import { FilingPreviewLine } from './FilingPreviewLine';
import type { PaigeDestinationsBridge } from './paige.global';
import { StorageLocationPicker } from './StorageLocationPicker';

export interface NucleusOneSetupProps extends FieldsProps {
  bridge: PaigeDestinationsBridge;
  /** The stored destination: Paige browses Nucleus One with its stored API key. */
  destinationId: string;
}

const placeWords = {
  legend: 'Project or folder',
  choose: 'Choose a project or folder',
  change: 'Change',
};

/**
 * A stored Nucleus One destination's place and fields, as CapturePoint's Nucleus One card: the picker walks
 * organizations, projects (a project means its Documents) and folders, with New folder; the field map offers the
 * project's own fields; the line below says where the job's latest document would be filed.
 */
export function NucleusOneSetup(props: NucleusOneSetupProps) {
  const { bridge, destinationId, draft, set } = props;
  const choose = (place: { id: string; path: string }) => {
    set('locationId')({ target: { value: place.id } });
    set('locationPath')({ target: { value: place.path } });
  };
  const path =
    draft.locationPath || [draft.organization, draft.project].filter(Boolean).join(' / ');
  return (
    <>
      <StorageLocationPicker
        bridge={bridge}
        destinationId={destinationId}
        path={path}
        search={null}
        onChoose={choose}
        words={placeWords}
      />
      <small>
        Documents are filed into folders from the job&apos;s file naming, made as needed in the
        chosen project or folder.
      </small>
      <label>
        Document classification
        <input value={draft.classification} onChange={set('classification')} autoComplete="off" />
      </label>
      <ProjectFieldMap {...props} />
      <FilingPreviewLine kind="nucleus-one" bridge={bridge} locationPath={path || 'Documents'} />
    </>
  );
}

/** The field map, with the chosen project's own fields offered as targets. */
function ProjectFieldMap({ bridge, destinationId, draft, setMap }: NucleusOneSetupProps) {
  const targets = useProjectFields(bridge, destinationId, draft.locationId);
  if (!setMap) return null;
  return (
    <FieldMapEditor
      map={draft.fieldMap}
      onChange={setMap}
      targetLabel="Nucleus One field"
      emptyTarget="Leave out"
      targets={targets}
      hint="Each field goes to the Nucleus One field of the same name. Map one to another name here, or leave the box empty to leave it out."
    />
  );
}

/** The fields of the project the chosen place is in (the saved place when none is chosen on screen). */
function useProjectFields(bridge: PaigeDestinationsBridge, destinationId: string, place: string) {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    bridge.n1Fields?.(destinationId, place || undefined).then(
      (fields) => live && setNames(fields.map((f) => f.name)),
      () => live && setNames([]),
    );
    return () => {
      live = false;
    };
  }, [bridge, destinationId, place]);
  return names;
}
